import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { WidgetLayout, WidgetOption } from '../widgets/useWidgetLayout';
import { useEffect, useRef, useState } from 'react';
import { Icons } from '@texas/components/shared/Icons';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';
import { widgetBoardMap } from '../widgets/widgets';
import { AddWidget } from '../widgets/shared/components/AddWidget';
import { Widget } from '../widgets/useWidgetLayout';
import { UsedOnBoard } from '../widgets/widgets';
import { WidgetOptions, WidgetType } from '../widgets/shared/types';

function filterLayoutByBoard(
  incoming: WidgetLayout,
  board: UsedOnBoard,
): WidgetLayout {
  const allowedWidgets = incoming.widgets.filter((w) => {
    const boards = widgetBoardMap[w.type];
    return boards.length === 0 || boards.includes(board);
  });
  const allowedIds = new Set(allowedWidgets.map((w) => w.layoutIndex));
  return {
    ...incoming,
    widgets: allowedWidgets,
    layout: Object.fromEntries(
      Object.entries(incoming.layout).map(([bp, items]) => [
        bp,
        items.filter((item) => allowedIds.has(item.i)),
      ]),
    ),
  };
}

interface Props {
  layout: WidgetLayout;
  setLayout: (layout: WidgetLayout) => void;
  widgetOptions: WidgetOption;
  defaultLayout: WidgetLayout;
  board: UsedOnBoard;
  currentWidgets: Widget[];
  onAdd: (
    type: WidgetType,
    name: string,
    color: string | null,
    options?: WidgetOptions,
  ) => void;
}

export function DashboardToolbar({
  layout,
  setLayout,
  widgetOptions,
  defaultLayout,
  board,
  currentWidgets,
  onAdd,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();

  const importModal = useDisclosure();
  const resetModal = useDisclosure();

  const [pastedJson, setPastedJson] = useState('');
  const [received, setReceived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (importModal.isOpen) {
      setPastedJson('');
      setReceived(false);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [importModal.isOpen]);

  function handleExport() {
    const json = JSON.stringify(layout, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      toast({
        title: t('widgets.export.copied'),
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    });
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    setPastedJson(text);
    setError(null);

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setError(t('widgets.import.invalidJson'));
      return;
    }

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('layout' in parsed) ||
      !('widgets' in parsed) ||
      !Array.isArray((parsed as WidgetLayout).widgets)
    ) {
      setError(t('widgets.import.invalidFormat'));
      return;
    }

    setReceived(true);
  }

  function handleImport() {
    const incoming = filterLayoutByBoard(
      JSON.parse(pastedJson) as WidgetLayout,
      board,
    );
    const merged: WidgetLayout = {
      ...incoming,
      widgets: incoming.widgets.map((w) => ({
        ...w,
        id: crypto.randomUUID(),
        widgetOptions:
          widgetOptions[w.type] !== undefined ? undefined : w.widgetOptions,
      })),
    };

    setLayout(merged);
    importModal.onClose();
    toast({
      title: t('widgets.import.success'),
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
  }

  function handleReset() {
    const merged: WidgetLayout = {
      ...defaultLayout,
      widgets: defaultLayout.widgets.map((w) => ({
        ...w,
        widgetOptions:
          widgetOptions[w.type] !== undefined ? undefined : w.widgetOptions,
      })),
    };
    setLayout(merged);
    resetModal.onClose();
    toast({
      title: t('widgets.reset.success'),
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
  }

  return (
    <>
      <Flex justify="space-between" align="center">
        <ButtonGroup isAttached={true} variant="entityDirectorySecondary">
          <AddWidget
            board={board}
            currentWidgets={currentWidgets}
            onAdd={onAdd}
          />
          <Menu isLazy={true}>
            <MenuButton
              as={IconButton}
              variant="entityDirectorySecondary"
              aria-label={t('general.more')}
              icon={<Icons.DotsHorizontal boxSize={4} />}
            />
            <Portal>
              <MenuList>
                <MenuItem
                  icon={<Icons.Upload boxSize={5} />}
                  onClick={handleExport}
                >
                  {t('widgets.export.button')}
                </MenuItem>
                <MenuItem
                  icon={<Icons.Download boxSize={5} />}
                  onClick={importModal.onOpen}
                >
                  {t('widgets.import.button')}
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  icon={<Icons.Refresh boxSize={5} />}
                  onClick={resetModal.onOpen}
                >
                  {t('widgets.reset.button')}
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </ButtonGroup>
      </Flex>

      <Modal
        isOpen={importModal.isOpen}
        onClose={importModal.onClose}
        size="sm"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>{t('widgets.import.title')}</ModalHeader>
          <ModalBody>
            <Flex flexDir="column" gap={3}>
              {received ? (
                <Box p={4} animation={fadeInScaleAnimation(500)}>
                  <Text>{t('widgets.import.received')}</Text>
                </Box>
              ) : (
                <>
                  <Text variant="sub">{t('widgets.import.description')}</Text>
                  <Input
                    ref={inputRef}
                    value=""
                    onPaste={handlePaste}
                    placeholder={t('widgets.import.placeholder')}
                  />
                  {error && (
                    <Text color="red.400" fontSize="sm">
                      {error}
                    </Text>
                  )}
                </>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button onClick={importModal.onClose}>{t('general.cancel')}</Button>
            <Button
              variant="texas-solid"
              isDisabled={!received}
              onClick={handleImport}
            >
              {t('widgets.import.apply')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={resetModal.isOpen} onClose={resetModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>{t('widgets.reset.title')}</ModalHeader>
          <ModalBody>
            <Text>{t('widgets.reset.description')}</Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button onClick={resetModal.onClose}>{t('general.cancel')}</Button>
            <Button colorScheme="red" onClick={handleReset}>
              {t('widgets.reset.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
